/**
 * Zustand store slice — content-gen admin (Manage tab) state.
 *
 * Tracks prompt libraries and their lazily-loaded prompts.
 * A library entry in promptsByLibrary === undefined means "not yet loaded";
 * an empty array means "loaded but empty".
 */

import { create } from 'zustand';
import type { PromptLibraryOut, PromptOut } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContentAdminState {
  libraries: PromptLibraryOut[];
  /** Prompts per library — undefined = not yet loaded for that library. */
  promptsByLibrary: Record<string, PromptOut[]>;
  /** Which library row is currently expanded. */
  selectedLibraryId: string | null;
  isLoading: boolean;
  contentAdminError: string | null;

  // -- Actions ---------------------------------------------------------------

  setLibraries: (libs: PromptLibraryOut[]) => void;
  addLibrary: (lib: PromptLibraryOut) => void;
  removeLibrary: (id: string) => void;
  setSelectedLibraryId: (id: string | null) => void;
  setPromptsForLibrary: (libraryId: string, prompts: PromptOut[]) => void;
  addPromptToLibrary: (libraryId: string, prompt: PromptOut) => void;
  updatePromptInLibrary: (libraryId: string, prompt: PromptOut) => void;
  removePromptFromLibrary: (libraryId: string, promptId: string) => void;
  setIsLoading: (v: boolean) => void;
  setContentAdminError: (e: string | null) => void;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useContentAdminStore = create<ContentAdminState>((set) => ({
  libraries: [],
  promptsByLibrary: {},
  selectedLibraryId: null,
  isLoading: false,
  contentAdminError: null,

  setLibraries: (libraries) => set({ libraries }),

  addLibrary: (lib) =>
    set((state) => ({ libraries: [lib, ...state.libraries] })),

  removeLibrary: (id) =>
    set((state) => {
      const { [id]: _removed, ...rest } = state.promptsByLibrary;
      return {
        libraries: state.libraries.filter((l) => l.id !== id),
        promptsByLibrary: rest,
        selectedLibraryId:
          state.selectedLibraryId === id ? null : state.selectedLibraryId,
      };
    }),

  setSelectedLibraryId: (id) =>
    set((state) => ({
      selectedLibraryId: state.selectedLibraryId === id ? null : id,
    })),

  setPromptsForLibrary: (libraryId, prompts) =>
    set((state) => ({
      promptsByLibrary: { ...state.promptsByLibrary, [libraryId]: prompts },
    })),

  addPromptToLibrary: (libraryId, prompt) =>
    set((state) => ({
      promptsByLibrary: {
        ...state.promptsByLibrary,
        [libraryId]: [...(state.promptsByLibrary[libraryId] ?? []), prompt],
      },
      libraries: state.libraries.map((l) =>
        l.id === libraryId
          ? { ...l, prompt_count: l.prompt_count + 1 }
          : l,
      ),
    })),

  updatePromptInLibrary: (libraryId, prompt) =>
    set((state) => ({
      promptsByLibrary: {
        ...state.promptsByLibrary,
        [libraryId]: (state.promptsByLibrary[libraryId] ?? []).map((p) =>
          p.id === prompt.id ? prompt : p,
        ),
      },
    })),

  removePromptFromLibrary: (libraryId, promptId) =>
    set((state) => ({
      promptsByLibrary: {
        ...state.promptsByLibrary,
        [libraryId]: (state.promptsByLibrary[libraryId] ?? []).filter(
          (p) => p.id !== promptId,
        ),
      },
      libraries: state.libraries.map((l) =>
        l.id === libraryId
          ? { ...l, prompt_count: Math.max(0, l.prompt_count - 1) }
          : l,
      ),
    })),

  setIsLoading: (isLoading) => set({ isLoading }),
  setContentAdminError: (contentAdminError) => set({ contentAdminError }),

  reset: () =>
    set({
      libraries: [],
      promptsByLibrary: {},
      selectedLibraryId: null,
      isLoading: false,
      contentAdminError: null,
    }),
}));
